import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Chip,
  Grid,
  Icon,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  useCotaRecuperacao,
  useRecuperacoes,
  useRecuperacao,
  usePreviaRecuperacao,
  useExecutarRecuperacao,
  useRepetirRecuperacao
} from "../../api/recuperacoes";
import {
  prepararPrevia,
  estadoRecuperacao,
  resumoRecuperacao,
  mensagemErroRecuperacao
} from "../../shared/recuperacaoAssistida";
import { dataExibicao } from "../../shared/apresentacaoEvento";

const initial = {
  origem: "GOVERNO",
  acao: "RECUPERAR",
  tpInsc: 1,
  nrInsc: "",
  cpf: "",
  competencia: "",
  tipos: [],
  repararInconsistentes: false,
  inicio: "",
  fim: "",
  ids: "",
  recibos: "",
  mapa: ""
};
const acoes = {
  PREVIA: "Prévia consultada",
  EXECUTAR: "Execução confirmada",
  REPETIR_FALHAS: "Falhas reagendadas",
  DOWNLOAD: "XML baixado",
  PRESERVAR_XML: "XML anterior preservado",
  RESULTADO: "Resultado",
  CONFERENCIA: "Conferência",
  RETORNO_FOLHA: "Retorno da Folha"
};
const campo = { fullWidth: true, size: "small" };

export default function RecuperacaoAssistida() {
  const [params, setParams] = useSearchParams();
  const id = params.get("recuperacao");
  const [form, setForm] = useState(initial);
  const [selecionados, setSelecionados] = useState([]);
  const [erro, setErro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [paginaItens, setPaginaItens] = useState(0);
  const historico = useRecuperacoes(pagina);
  const detalhe = useRecuperacao(id);
  const previa = usePreviaRecuperacao();
  const executar = useExecutarRecuperacao(id);
  const repetir = useRepetirRecuperacao(id);
  const r = detalhe.data;
  const enviando = useRef(false);
  const filtrosCota = r?.operacao?.filtros
    ? JSON.parse(r.operacao.filtros)
    : form;
  const consultaExterna = filtrosCota.origem !== "LOCAL";
  const cota = useCotaRecuperacao(
    filtrosCota.tpInsc,
    filtrosCota.nrInsc,
    consultaExterna
  );
  const situacaoCota = consultaExterna ? cota.data : null;
  const itens = r?.itens || [];
  const disponiveis = itens
    .filter((i) => i.estado === "DISPONIVEL")
    .map((i) => i.id);
  const selecao = selecionados.filter((i) => disponiveis.includes(i));
  const resumo = resumoRecuperacao(r);
  const indisponivel = historico.error?.status === 404;
  const ocupado =
    indisponivel || previa.isLoading || executar.isLoading || repetir.isLoading;
  useEffect(() => {
    setSelecionados([]);
    setPaginaItens(0);
    setErro("");
  }, [id]);
  function mudar(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  function abrir(operacao) {
    const next = new URLSearchParams(params);
    if (operacao) next.set("recuperacao", operacao);
    else next.delete("recuperacao");
    next.set("aba", "recuperacao");
    setParams(next);
  }
  async function consultar(event, aposId = 0) {
    event?.preventDefault();
    if (ocupado || enviando.current) return;
    enviando.current = true;
    setErro("");
    try {
      const dados = await previa.mutateAsync(prepararPrevia(form, aposId));
      abrir(dados.operacao.id);
    } catch (e) {
      setErro(e);
    } finally {
      enviando.current = false;
    }
  }
  async function confirmar() {
    setErro("");
    try {
      await executar.mutateAsync(selecao);
      setSelecionados([]);
    } catch (e) {
      setErro(e);
    }
  }
  async function retomar() {
    setErro("");
    try {
      await repetir.mutateAsync();
    } catch (e) {
      setErro(e);
    }
  }
  async function proximaLocal() {
    setErro("");
    try {
      const pedido = JSON.parse(r.operacao.filtros);
      const dados = await previa.mutateAsync({
        ...pedido,
        aposId: r.ultimoIdLocal
      });
      abrir(dados.operacao.id);
    } catch (e) {
      setErro(e);
    }
  }
  const erroAtual = erro || detalhe.error || previa.error;
  return (
    <Stack spacing={2}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
          spacing={2}
        >
          <Box>
            <Typography variant="h6">Recuperação de totalizadores</Typography>
            <Typography color="text.secondary" variant="body2">
              Consulte, revise os itens e acompanhe a entrega à Folha.
            </Typography>
          </Box>
          {id && (
            <Button
              startIcon={<Icon>add</Icon>}
              onClick={() => abrir(null)}
              disabled={ocupado}
            >
              Nova prévia
            </Button>
          )}
        </Stack>
        {situacaoCota && (
          <Alert
            severity={situacaoCota.bloqueado ? "warning" : "info"}
            sx={{ mt: 2 }}
          >
            <Typography variant="subtitle2">
              {situacaoCota.bloqueado
                ? "Novas chamadas ao eSocial bloqueadas"
                : "Consultas e downloads do eSocial"}
            </Typography>
            {situacaoCota.acessosRegistrados} de {situacaoCota.limite} chamadas
            registradas neste conector nas últimas 24 horas. Outros sistemas
            também podem consumir a cota; este contador não é o saldo oficial.
            {situacaoCota.bloqueado && (
              <>
                {" "}
                Proteção local até {dataExibicao(situacaoCota.novaJanelaEm)}.
                Use dados locais ou consulte respostas já guardadas.
              </>
            )}
            {situacaoCota.emAndamento && (
              <>
                {" "}
                Há uma consulta ou download em andamento para este empregador.
              </>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              Buscas idênticas reutilizam respostas guardadas por até{" "}
              {situacaoCota.cacheHoras} horas, inclusive quando não há
              resultados. Novos eventos podem não aparecer durante esse período.
              Cada prévia por competência pode consumir até três chamadas; o
              download também usa a mesma cota.
            </Typography>
          </Alert>
        )}
        {consultaExterna && cota.error && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Não foi possível carregar o consumo local. O backend continua
            controlando a cota.
          </Alert>
        )}
        {!id && (
          <Box component="form" onSubmit={consultar} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  {...campo}
                  select
                  label="O que deseja fazer?"
                  value={form.acao}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      acao: e.target.value,
                      origem:
                        e.target.value === "CONSOLIDAR" ? "LOCAL" : f.origem,
                      tipos: e.target.value === "CONSOLIDAR" ? ["S5002"] : [],
                      repararInconsistentes: false
                    }))
                  }
                >
                  <MenuItem value="RECUPERAR">
                    Recuperar e conferir totalizadores na Folha
                  </MenuItem>
                  <MenuItem value="CONSOLIDAR">
                    Conferir e consolidar IRRF (S-5002)
                  </MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  {...campo}
                  select
                  label="Onde consultar?"
                  value={form.origem}
                  disabled={form.acao === "CONSOLIDAR"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      origem: e.target.value,
                      tipos: [],
                      repararInconsistentes: false
                    }))
                  }
                >
                  <MenuItem value="GOVERNO_EMPREGADOR">
                    No eSocial, por empregador e competência
                  </MenuItem>
                  <MenuItem value="GOVERNO">
                    No eSocial, por CPF e período de recepção
                  </MenuItem>
                  <MenuItem value="LOCAL">
                    No conector, por competência
                  </MenuItem>
                  <MenuItem value="MANUAL">
                    IDs ou recibos que já possuo
                  </MenuItem>
                </TextField>
              </Grid>
              {form.origem !== "LOCAL" && (
                <>
                  <Grid item xs={12} sm={4} md={3}>
                    <TextField
                      {...campo}
                      select
                      label="Tipo de inscrição"
                      value={form.tpInsc}
                      onChange={(e) => mudar("tpInsc", Number(e.target.value))}
                    >
                      <MenuItem value={1}>CNPJ</MenuItem>
                      <MenuItem value={2}>CPF</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={8} md={9}>
                    <TextField
                      {...campo}
                      required
                      label="Inscrição do empregador"
                      value={form.nrInsc}
                      onChange={(e) => mudar("nrInsc", e.target.value)}
                      inputProps={{ maxLength: 18 }}
                    />
                  </Grid>
                </>
              )}
              {form.origem !== "GOVERNO_EMPREGADOR" && (
                <Grid item xs={12} md={4}>
                  <TextField
                    {...campo}
                    label="CPF do trabalhador"
                    required={form.origem === "GOVERNO"}
                    value={form.cpf}
                    onChange={(e) => mudar("cpf", e.target.value)}
                    inputProps={{ maxLength: 14 }}
                    helperText={
                      form.origem === "LOCAL"
                        ? "Filtra os eventos do trabalhador; o fechamento continua incluído."
                        : undefined
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  {...campo}
                  label="Competência"
                  placeholder="2026-09 ou 2026"
                  required={["LOCAL", "GOVERNO_EMPREGADOR"].includes(
                    form.origem
                  )}
                  value={form.competencia}
                  onChange={(e) => mudar("competencia", e.target.value)}
                  inputProps={{ maxLength: 7 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  {...campo}
                  select
                  label="Tipos de totalizador"
                  value={form.tipos}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (values) =>
                      values.length
                        ? values.map((t) => t.replace("S", "S-")).join(", ")
                        : "Todos os tipos disponíveis"
                  }}
                  InputLabelProps={{ shrink: true }}
                  disabled={form.acao === "CONSOLIDAR"}
                  onChange={(e) => mudar("tipos", e.target.value)}
                >
                  {(form.origem === "GOVERNO_EMPREGADOR"
                    ? ["S5011", "S5012"]
                    : form.origem === "GOVERNO"
                    ? ["S5001", "S5002", "S5003"]
                    : ["S5001", "S5002", "S5003", "S5011", "S5012"]
                  ).map((t) => (
                    <MenuItem key={t} value={t}>
                      <Checkbox checked={form.tipos.includes(t)} size="small" />
                      {t.replace("S", "S-")}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {form.acao === "RECUPERAR" && form.origem !== "GOVERNO" && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.repararInconsistentes}
                        onChange={(e) =>
                          mudar("repararInconsistentes", e.target.checked)
                        }
                      />
                    }
                    label="Corrigir S-5011/S-5012 com XML incompatível, preservando o original no histórico"
                  />
                </Grid>
              )}
              {form.origem === "GOVERNO" && (
                <>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...campo}
                      required
                      type="datetime-local"
                      label="Recepção inicial"
                      InputLabelProps={{ shrink: true }}
                      value={form.inicio}
                      onChange={(e) => mudar("inicio", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      {...campo}
                      required
                      type="datetime-local"
                      label="Recepção final"
                      InputLabelProps={{ shrink: true }}
                      value={form.fim}
                      onChange={(e) => mudar("fim", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Consulte até 31 dias por vez. A competência será conferida
                      no XML recebido.
                    </Typography>
                  </Grid>
                </>
              )}
              {form.origem === "MANUAL" && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      {...campo}
                      multiline
                      minRows={3}
                      label="IDs dos totalizadores"
                      value={form.ids}
                      onChange={(e) => mudar("ids", e.target.value)}
                      helperText="Um por linha ou separados por vírgula."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      {...campo}
                      multiline
                      minRows={3}
                      label="Recibos próprios dos totalizadores"
                      value={form.recibos}
                      onChange={(e) => mudar("recibos", e.target.value)}
                      helperText="Use o recibo do totalizador; o recibo do evento de origem é diferente."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      {...campo}
                      multiline
                      minRows={2}
                      label="Correspondência ID=RECIBO (opcional)"
                      value={form.mapa}
                      onChange={(e) => mudar("mapa", e.target.value)}
                    />
                  </Grid>
                </>
              )}
              {form.acao === "CONSOLIDAR" && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    A prévia verifica os pagamentos e o histórico na Folha.
                    Registros excluídos, superados ou divergentes exigem
                    revisão.
                  </Alert>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={ocupado || situacaoCota?.emAndamento}
                  startIcon={<Icon>search</Icon>}
                >
                  {previa.isLoading
                    ? "Consultando…"
                    : situacaoCota?.bloqueado && form.origem !== "MANUAL"
                    ? "Consultar dados guardados"
                    : "Consultar prévia"}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        {erroAtual && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {mensagemErroRecuperacao(erroAtual, !!id)}
          </Alert>
        )}
        {(detalhe.isLoading || previa.isLoading) && (
          <LinearProgress aria-label="Consultando recuperação" sx={{ mt: 2 }} />
        )}
      </Paper>
      {r && (
        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography variant="h6">
                  {r.operacao.acao === "CONSOLIDAR"
                    ? "Consolidação de IRRF"
                    : "Entrega de totalizadores"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Solicitada por {r.operacao.usuario} ·{" "}
                  {dataExibicao(r.operacao.criadaEm)}
                </Typography>
              </Box>
              <Chip
                label={
                  !r.operacao.executar
                    ? "Prévia para revisão"
                    : r.emAndamento
                    ? "Recuperação em andamento"
                    : r.aguardandoFolha
                    ? "Aguardando a Folha"
                    : "Processamento encerrado"
                }
              />
            </Stack>
            <Filtros operacao={r.operacao} />
            {r.operacao.origem === "LOCAL" &&
              r.operacao.acao === "RECUPERAR" && (
                <Button
                  variant="outlined"
                  startIcon={<Icon>search</Icon>}
                  onClick={() => {
                    const filtros = JSON.parse(r.operacao.filtros);
                    setForm((f) => ({
                      ...f,
                      origem: "GOVERNO_EMPREGADOR",
                      acao: "RECUPERAR",
                      competencia: filtros.competencia || "",
                      cpf: "",
                      tipos: (filtros.tipos || [filtros.tipo]).filter((t) =>
                        ["S5011", "S5012"].includes(t)
                      ),
                      repararInconsistentes: false
                    }));
                    abrir(null);
                  }}
                >
                  Buscar fechamento no eSocial
                </Button>
              )}
            {r.operacao.aviso && (
              <Alert severity="warning">{r.operacao.aviso}</Alert>
            )}
            <Grid container spacing={1}>
              {resumo.map((c) => (
                <Grid item xs={6} md={3} key={c.label}>
                  <Box sx={{ bgcolor: "action.hover", p: 2, height: "100%" }}>
                    <Typography variant="h5">{c.valor}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {c.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            {r.emAndamento && (
              <Box>
                <LinearProgress />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Você pode sair desta tela. A recuperação continuará e poderá
                  ser consultada no histórico.
                </Typography>
              </Box>
            )}
            {r.aguardandoFolha && (
              <Alert severity="info">
                Há entregas pendentes de confirmação pela Folha. Os arquivos
                recuperados permanecem guardados no conector.
              </Alert>
            )}
            {!itens.length ? (
              <Alert severity="info">
                Nenhum totalizador encontrado para este recorte.
              </Alert>
            ) : (
              <>
                {!r.operacao.executar && disponiveis.length > 0 && (
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    spacing={1}
                  >
                    <Typography alignSelf="center">
                      {selecao.length} de {disponiveis.length} disponíveis
                      selecionados
                    </Typography>
                    <Button
                      variant="contained"
                      disabled={!selecao.length || ocupado}
                      onClick={confirmar}
                      startIcon={<Icon>play_arrow</Icon>}
                    >
                      {r.operacao.acao === "CONSOLIDAR"
                        ? "Confirmar consolidação"
                        : "Confirmar recuperação"}{" "}
                      ({selecao.length})
                    </Button>
                  </Stack>
                )}
                <TableContainer>
                  <Table size="small" aria-label="Itens da recuperação">
                    <TableHead>
                      <TableRow>
                        {!r.operacao.executar && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              inputProps={{
                                "aria-label": "Selecionar todos os disponíveis"
                              }}
                              disabled={!disponiveis.length || ocupado}
                              checked={
                                !!disponiveis.length &&
                                selecao.length === disponiveis.length
                              }
                              indeterminate={
                                selecao.length > 0 &&
                                selecao.length < disponiveis.length
                              }
                              onChange={(e) =>
                                setSelecionados(
                                  e.target.checked ? disponiveis : []
                                )
                              }
                            />
                          </TableCell>
                        )}
                        <TableCell>Tipo / competência</TableCell>
                        <TableCell>CPF / recibo de origem</TableCell>
                        <TableCell>Situação</TableCell>
                        <TableCell>Detalhes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {itens
                        .slice(paginaItens * 25, paginaItens * 25 + 25)
                        .map((i) => (
                          <Linha
                            key={i.id}
                            item={i}
                            previa={!r.operacao.executar}
                            marcado={selecao.includes(i.id)}
                            ocupado={ocupado}
                            mudar={(marcado) =>
                              setSelecionados((v) =>
                                marcado
                                  ? [...v, i.id]
                                  : v.filter((x) => x !== i.id)
                              )
                            }
                          />
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                {itens.length > 25 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={1}
                  >
                    <Button
                      disabled={!paginaItens}
                      onClick={() => setPaginaItens(paginaItens - 1)}
                    >
                      Anterior
                    </Button>
                    <Typography variant="body2">
                      {paginaItens + 1} / {Math.ceil(itens.length / 25)}
                    </Typography>
                    <Button
                      disabled={(paginaItens + 1) * 25 >= itens.length}
                      onClick={() => setPaginaItens(paginaItens + 1)}
                    >
                      Próxima
                    </Button>
                  </Stack>
                )}
              </>
            )}
            {itens.some(
              (i) =>
                i.selecionado && ["FALHA", "FALHA_FOLHA"].includes(i.estado)
            ) && (
              <Button
                sx={{ alignSelf: "flex-start" }}
                onClick={retomar}
                disabled={ocupado}
                startIcon={<Icon>refresh</Icon>}
              >
                Repetir somente as falhas
              </Button>
            )}
            {r.operacao.origem === "LOCAL" &&
              r.operacao.aviso?.includes("mais registros locais") && (
                <Button
                  sx={{ alignSelf: "flex-start" }}
                  onClick={proximaLocal}
                  disabled={ocupado}
                >
                  Consultar próxima página de totalizadores
                </Button>
              )}
            <Accordion
              disableGutters
              elevation={0}
              sx={{
                border: 1,
                borderColor: "divider",
                "&:before": { display: "none" }
              }}
            >
              <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
                <Typography>Auditoria da operação</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Até 200 registros mais recentes. Operação {r.operacao.id}
                </Typography>
                <TableContainer>
                  <Table size="small" aria-label="Auditoria da recuperação">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Usuário</TableCell>
                        <TableCell>Ação / item</TableCell>
                        <TableCell>Resultado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {r.auditoria.map((a) => (
                        <TableRow key={a.id}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {dataExibicao(a.data)}
                          </TableCell>
                          <TableCell>{a.usuario}</TableCell>
                          <TableCell>
                            {acoes[a.acao] || a.acao}
                            {a.itemId ? " · #" + a.itemId : ""}
                          </TableCell>
                          <TableCell>
                            {a.detalhe?.replace(/^[A-Z_]+: /, "")}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Stack>
        </Paper>
      )}
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Typography variant="h6" gutterBottom>
          Histórico de recuperações
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Abra uma operação para acompanhar, revisar a auditoria ou retomar
          falhas.
        </Typography>
        {historico.isFetching && (
          <LinearProgress aria-label="Consultando histórico" />
        )}
        {historico.error && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                disabled={historico.isFetching}
                onClick={() => historico.refetch()}
              >
                Verificar novamente
              </Button>
            }
          >
            {mensagemErroRecuperacao(historico.error)}
          </Alert>
        )}
        {!historico.error && historico.data?.length === 0 && (
          <Typography color="text.secondary">
            Nenhuma operação nesta página.
          </Typography>
        )}
        <Stack
          divider={<Box sx={{ borderBottom: 1, borderColor: "divider" }} />}
        >
          {historico.data?.map((o) => (
            <Stack
              key={o.id}
              direction="row"
              justifyContent="space-between"
              spacing={2}
              sx={{ py: 1.5 }}
            >
              <Box>
                <Typography>
                  {o.acao === "CONSOLIDAR"
                    ? "Consolidação de IRRF"
                    : "Recuperação"}{" "}
                  · {o.usuario}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {dataExibicao(o.criadaEm)} ·{" "}
                  {o.origem === "LOCAL"
                    ? "Consulta local"
                    : ["GOVERNO", "GOVERNO_EMPREGADOR"].includes(o.origem)
                    ? "Consulta no eSocial"
                    : "Identificadores manuais"}{" "}
                  · {o.executar ? "Confirmada" : "Prévia"}
                </Typography>
              </Box>
              <Button onClick={() => abrir(o.id)} disabled={ocupado}>
                Abrir
              </Button>
            </Stack>
          ))}
        </Stack>
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mt: 1 }}
        >
          <Button
            disabled={!pagina || historico.isFetching}
            onClick={() => setPagina(pagina - 1)}
          >
            Anteriores
          </Button>
          <Typography variant="body2">Página {pagina + 1}</Typography>
          <Button
            disabled={historico.data?.length !== 20 || historico.isFetching}
            onClick={() => setPagina(pagina + 1)}
          >
            Próximas
          </Button>
        </Stack>
      </Paper>
    </Stack>
  );
}
function Filtros({ operacao }) {
  let f = {};
  try {
    f = JSON.parse(operacao.filtros);
  } catch (e) {
    /* A API mantém o recorte original. */
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {f.competencia && (
        <Chip size="small" label={"Competência " + f.competencia} />
      )}
      {(f.tipos || (f.tipo ? [f.tipo] : [])).map((t) => (
        <Chip key={t} size="small" label={t.replace("S", "S-")} />
      ))}
      {f.repararInconsistentes && (
        <Chip
          size="small"
          color="warning"
          label="Correção de XML incompatível autorizada"
        />
      )}
      {f.cpf && <Chip size="small" label={"CPF " + f.cpf} />}
      {f.inicio && (
        <Chip
          size="small"
          label={
            "Recepção " +
            f.inicio.replace("T", " ") +
            " a " +
            f.fim.replace("T", " ")
          }
        />
      )}
      {operacao.origem === "MANUAL" && (
        <Chip size="small" label="Identificadores informados manualmente" />
      )}
    </Stack>
  );
}
function Linha({ item: i, previa, marcado, mudar, ocupado }) {
  const [aberto, setAberto] = useState(false);
  const estado = estadoRecuperacao(i.estado);
  return (
    <>
      <TableRow hover>
        {previa && (
          <TableCell padding="checkbox">
            <Checkbox
              inputProps={{ "aria-label": "Selecionar item " + i.id }}
              checked={marcado}
              disabled={i.estado !== "DISPONIVEL" || ocupado}
              onChange={(e) => mudar(e.target.checked)}
            />
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2">
            {i.tipo?.replace("S", "S-") || "A identificar"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {i.competencia ||
              (i.estado === "JA_EXISTENTE"
                ? "Não consultada"
                : "Conferida após download")}
          </Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{i.cpf || "—"}</Typography>
          <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
            {i.reciboBase || "Recibo ainda não disponível"}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip size="small" color={estado.cor} label={estado.texto} />
        </TableCell>
        <TableCell>
          <Button
            size="small"
            onClick={() => setAberto(!aberto)}
            aria-expanded={aberto}
          >
            {aberto ? "Fechar" : "Ver"}
          </Button>
        </TableCell>
      </TableRow>
      {aberto && (
        <TableRow>
          <TableCell
            colSpan={previa ? 5 : 4}
            sx={{ bgcolor: "action.hover", p: 2 }}
          >
            <Stack spacing={1}>
              <Typography variant="body2">{i.mensagem}</Typography>
              {i.resultadoFolha && (
                <Typography variant="body2">
                  Resultado informado pela Folha:{" "}
                  {i.resultadoFolha === "APLICADA"
                    ? "Entrega aplicada"
                    : i.resultadoFolha}
                </Typography>
              )}
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip
                  size="small"
                  variant="outlined"
                  label={
                    i.arquivoGuardado
                      ? "XML guardado"
                      : "XML ainda não recuperado"
                  }
                />
                {i.localId && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={"Registro local #" + i.localId}
                  />
                )}
                {i.pendenciaId && (
                  <Button
                    size="small"
                    component={Link}
                    to={"/pendencias?id=" + i.pendenciaId}
                  >
                    Entrega #{i.pendenciaId}
                  </Button>
                )}
              </Stack>
              <Typography variant="caption" sx={{ overflowWrap: "anywhere" }}>
                ID: {i.idEsocial || "—"} · Recibo próprio: {i.recibo || "—"}
              </Typography>
              <Typography variant="caption">
                Tentativas de recuperação: {i.tentativas} · Atualização:{" "}
                {dataExibicao(i.atualizadoEm)}
                {i.codigo ? " · " + i.codigo : ""}
                {i.codigoFolha ? " · Folha: " + i.codigoFolha : ""}
                {i.aplicadaEmFolha
                  ? " · Aplicada na Folha em " + dataExibicao(i.aplicadaEmFolha)
                  : ""}
              </Typography>
            </Stack>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
